// Detects Datadog Synthetics browser-test (bot) sessions so we can exclude them from RUM.
// Datadog recommends combining signals because each has gaps:
// https://docs.datadoghq.com/synthetics/guide/identify_synthetics_bots/
//   - `window._DATADOG_SYNTHETICS_BROWSER` is set on every bot-rendered page (Chrome/Edge/
//     most), but on Firefox is not guaranteed to be set before our code runs.
//   - The public-id/result-id signals cover the Firefox case (all steps), but on Chrome/Edge
//     the cookies are set only for the start URL. These match how RUM tags session type as
//     'synthetics' vs 'user' (mirrors @datadog/browser-core's internal syntheticsWorkerValues,
//     which the SDK does not export publicly), true only when BOTH ids are present.
// OR-ing them follows Datadog's "combine several methods" guidance.

const SYNTHETICS_BROWSER_VAR = '_DATADOG_SYNTHETICS_BROWSER';
const SYNTHETICS_TEST_ID_VAR = '_DATADOG_SYNTHETICS_PUBLIC_ID';
const SYNTHETICS_RESULT_ID_VAR = '_DATADOG_SYNTHETICS_RESULT_ID';
const SYNTHETICS_TEST_ID_COOKIE = 'datadog-synthetics-public-id';
const SYNTHETICS_RESULT_ID_COOKIE = 'datadog-synthetics-result-id';

function getCookie(name: string): string | undefined {
  const match = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}

function readValue(varName: string, cookieName: string): string | undefined {
  const fromWindow = (window as unknown as Record<string, unknown>)[varName];
  if (typeof fromWindow === 'string' && fromWindow !== '') {
    return fromWindow;
  }
  return getCookie(cookieName);
}

export function isSyntheticsSession(): boolean {
  // SSR-safe: no window/document during server render, so it cannot be a synthetics session.
  if (typeof window === 'undefined') {
    return false;
  }
  if ((window as unknown as Record<string, unknown>)[SYNTHETICS_BROWSER_VAR]) {
    return true;
  }
  const testId = readValue(SYNTHETICS_TEST_ID_VAR, SYNTHETICS_TEST_ID_COOKIE);
  const resultId = readValue(SYNTHETICS_RESULT_ID_VAR, SYNTHETICS_RESULT_ID_COOKIE);
  return Boolean(testId && resultId);
}
